import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { Menu, X, LogOut, Home } from "lucide-react";
import { LogoSymbol, Wordmark } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";

export default function DashboardLayout({ items, title, accent, headerExtra }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = (
    <nav className="space-y-0.5">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} data-testid={`sidebar-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-[#D4AF63]/15 text-[#E6C77B] ring-1 ring-[#D4AF63]/30" : "text-[#FAF8F2]/75 hover:text-[#FAF8F2] hover:bg-white/5"}`}>
          <Icon className="h-4 w-4 shrink-0" /> {label}
        </NavLink>
      ))}
    </nav>
  );
  const side = (
    <div className="flex flex-col h-full">
      <Link to="/" className="flex items-center gap-2.5 px-2 mb-2"><LogoSymbol size={34} /><Wordmark light size="text-xl" /></Link>
      <div className="px-2 mb-6 text-[11px] uppercase tracking-[0.2em] text-teal-300 font-semibold">{title}</div>
      <div className="flex-1 overflow-y-auto pr-1">{nav}</div>
      <div className="pt-4 border-t border-white/10 mt-4 space-y-1">
        <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-slate-300 hover:text-white"><Home className="h-4 w-4" /> Public site</Link>
        <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-slate-300 hover:text-white" data-testid="sidebar-logout-btn"><LogOut className="h-4 w-4" /> Sign out</button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-[#FAF8F2] lg:flex">
      <aside className="hidden lg:block w-64 shrink-0 bg-[#003B2D] p-4 sticky top-0 h-screen">{side}</aside>
      {open && <div className="fixed inset-0 z-50 lg:hidden"><div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} /><aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#003B2D] p-4">{side}</aside></div>}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200 h-16 px-4 sm:px-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setOpen(true)} data-testid="dashboard-menu-toggle" aria-label="Menu">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
            {headerExtra}
          </div>
          <div className="flex items-center gap-3">
            {accent}
            <NotificationBell />
            <div className="hidden sm:block text-right">
              <div className="text-sm font-semibold text-slate-800 leading-tight" data-testid="dashboard-user-name">{user?.name}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
          </div>
        </header>
        {user && !user.email_verified && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 sm:px-8 py-2" data-testid="verify-email-banner">
            Please verify your email address to secure your account. <Link to="/verify-email" className="underline font-semibold">Resend verification</Link>
          </div>
        )}
        <main className="px-4 sm:px-8 py-8 max-w-7xl"><Outlet /></main>
      </div>
    </div>
  );
}
